/**
 * Map trainer narrator history to concise on-screen cues at exact word-synced times.
 */

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function cueEndForRow(row) {
  if (row?.cue_end != null) return num(row.cue_end);
  return num(row.narration_end, num(row.global_time) + 30);
}

/** Active narrator cue at playhead from rendered.narrator_history. */
export function activeNarratorCueAtPlayhead(rendered, playhead) {
  const history = narratorHistoryForRendered(rendered);
  if (!history.length) return null;

  const t = num(playhead);
  let active = null;
  for (const row of history) {
    const start = num(row.global_time);
    const end = cueEndForRow(row);
    if (start <= t + 0.02 && t < end) {
      if (!active || start >= num(active.global_time)) {
        active = row;
      }
    }
  }
  return active;
}

/** Cumulative code highlight line from narrator history (TYPE_LINE steps). */
export function codeHighlightFromNarratorHistory(rendered, playhead) {
  const history = narratorHistoryForRendered(rendered);
  const t = num(playhead);
  let line = null;
  for (const row of history) {
    const start = num(row.global_time);
    if (start > t + 0.02) break;
    const at = String(row.artifact_type || '').toLowerCase();
    if (!at.includes('code')) continue;
    const stepLine = row.line != null ? num(row.line) : num(row.step_index) + 1;
    if (stepLine > 0) line = Math.max(line ?? 0, stepLine);
  }
  return line;
}

/** Max flowchart step index visible at playhead (cumulative node reveal). */
export function flowchartStepIndexAtPlayhead(rendered, playhead, nodePath = '') {
  const history = narratorHistoryForRendered(rendered);
  const t = num(playhead);
  const path = String(nodePath || '');
  let maxStep = -1;
  for (const row of history) {
    const start = num(row.global_time);
    if (start > t + 0.02) break;
    if (path && row.node_path && row.node_path !== path) continue;
    const at = String(row.artifact_type || '').toLowerCase();
    if (at !== 'flowchart' && !at.includes('flow')) continue;
    if (row.step_index != null) maxStep = Math.max(maxStep, num(row.step_index));
  }
  return maxStep;
}

/** Upcoming cues within lookahead seconds (for debug / timeline lanes). */
export function upcomingNarratorCues(rendered, playhead, lookaheadSec = 8) {
  const history = rendered?.narrator_history;
  if (!Array.isArray(history)) return [];
  const t = num(playhead);
  const end = t + Math.max(0, lookaheadSec);
  return history.filter((row) => {
    const start = num(row.global_time);
    return start >= t && start <= end;
  });
}

/** Build fallback history from elements when backend narrator_history is absent. */
export function buildNarratorHistoryFallback(rendered) {
  const elements = rendered?.elements || [];
  const timings = rendered?.narration_word_timings || [];
  const byId = Object.fromEntries(
    timings.map((r) => [String(r.element_id || ''), r]),
  );
  const rows = [];
  for (const el of elements) {
    const narr = String(el.narration || '').trim();
    if (!narr) continue;
    const eid = String(el.id || '');
    const words = byId[eid]?.words || el.word_timestamps || [];
    const start = num(el.narration_start ?? el.start);
    const end = num(el.narration_end ?? el.end, start + 1);
    const concise = narr.split(/\s+/).slice(0, 12).join(' ');
    rows.push({
      node_path: (el.node_path || []).join('/'),
      node_title: (el.node_path || []).slice(-1)[0] || '',
      artifact_type: el.artifact_type || el.type || 'narration',
      step_index: 0,
      concise_text: concise,
      global_time: start,
      narration_end: end,
      trigger_word: words[0]?.token || words[0]?.word || '',
      action: 'SHOW',
    });
  }
  return rows.sort((a, b) => num(a.global_time) - num(b.global_time));
}

export function narratorHistoryForRendered(rendered) {
  if (rendered?.narrator_history?.length) {
    return rendered.narrator_history;
  }
  return buildNarratorHistoryFallback(rendered);
}
